import { useRef, useState } from "react";
import { IoMdClose } from "react-icons/io";
import ProductName from "./ProductName";
import ProductDescription from "./ProductDescription";
import PriceInput from "./PriceInput";
import QuantityInput from "./QuantityInput";
import CategoryInput from "./CategoryInput";
import {
  Branch,
  Categories,
  Category,
  ProductBranch,
} from "../../types/globalTypes";
import { FormikErrors, useFormik } from "formik";
import * as Yup from "yup";
import ProductOptionGroup from "./ProductOptionGroup";
import {
  addon,
  option,
  OptionGroup,
  QuantityType,
} from "../../types/ProductType";
import ProductAddon from "./ProductAddon";
import toast from "react-hot-toast";
import axios from "axios";
import { useSelector } from "react-redux";
import ProductBranchesInput from "./ProductBranchesInput";
import { BsPlusCircleFill } from "react-icons/bs";
import { MdKeyboardArrowDown } from "react-icons/md";
import { Cropper, ReactCropperElement } from "react-cropper";
import { AppConfig } from "@/utils/AppConfig";
import { formatMoneyForInput, getMaxImageSize } from "@/utils/helpers";
import ProductCostInput from "./ProductCostInput";
import { useTranslation } from "react-i18next";

import { useUserRole } from "@/hooks/useUserRole";
import useLockBodyScroll from "@/hooks/useLockBodyScroll";

const { MAX_IMAGE_SIZE, MAX_IMAGE_SIZE_MB_String } = getMaxImageSize(
  AppConfig.ImagesMaxSizeInMB.products,
);

type FormikError = string | FormikError[] | { [key: string]: FormikError };
const flattenErrors = (input: FormikError[]): string[] => {
    const result: string[] = [];
    
    const extractStrings = (item: FormikError) => {
        if (typeof item === 'string') {
            result.push(item);
        } else if (Array.isArray(item)) {
            item.forEach(extractStrings);
        } else if (typeof item === 'object' && item !== null) {
            Object.values(item).forEach(extractStrings);
        }
    };
    
    input.forEach(extractStrings);
    return result;
};


type FormValues = {
  nameAR: string;
  nameEN: string;
  categories: Category[];
  price: number | string;
  quantity: QuantityType | string;
  image: File | null | string;

  productCost?: number;
  calories: number | string;
  salt: number | string;
  caffeine: number | string;
  lowPrice: number | string;
  lowPriceEndDate: number | string;
  descriptionAR: string;
  descriptionEN: string;
  addons: addon[];
  options: OptionGroup[];
  branches: ProductBranch[];
};

export default function AddProductButton({
  allUserCategories,
  allBranches,
  setNeedRefetch,
}: {
  allUserCategories: Categories;
  allBranches: Branch[];
  setNeedRefetch: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const [isNutritionOpen, setIsNutritionOpen] = useState(false);

  const { isAdmin } = useUserRole();
  // Original data before change
  const [originalPhoto, setOriginalPhoto] = useState<string | undefined>(
    undefined,
  );
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  // cropper
  const [cropedOpen, setCropedOpen] = useState(false);
  const cropperRef = useRef<ReactCropperElement>(null);
  const [isSending, setIsSending] = useState(false);
  // other logic
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const modalRef = useRef<HTMLDivElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | undefined>(undefined);
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );
  useLockBodyScroll(cropedOpen || isModalOpen);

  const [addonIds, setAddonIds] = useState<number[]>([]);
  const [isLowPriceFoucused, setIsLowPriceFocused] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];

    if (selectedFile && selectedFile?.size > MAX_IMAGE_SIZE) {
      toast.error(
        t("addProductButton.fileTooLarge", {
          maxSize:
            locale === "ar"
              ? MAX_IMAGE_SIZE_MB_String.ar
              : MAX_IMAGE_SIZE_MB_String.en,
        }) as string,
      );
      e.target.value = "";
      return;
    }

    if (selectedFile) {
      setOriginalPhoto(preview);
      setOriginalFile(file);

      // Set the new file and photo
      setFile(selectedFile);
      setPreview(URL.createObjectURL(selectedFile));
      toggleCropedOpen(); // Open the cropper modal
    }
    e.target.value = ""; // Reset the file input
  };

  const onCloseCropper = () => {
    // Reset to the original photo and file
    setPreview(originalPhoto);
    setFile(originalFile);
    formik.setFieldValue("image", originalFile); // Reset Formik value

    // Close the cropper modal
    setCropedOpen(false);

    // Clear the original state
    setOriginalPhoto(undefined);
    setOriginalFile(null);
  };
  const toggleCropedOpen = () => {
    setCropedOpen((prev) => !prev);
  };

  // Function to handle cropping
  const onCrop = () => {
    if (cropperRef.current) {
      const cropper = cropperRef.current.cropper;
      const canvas = cropper.getCroppedCanvas({ width: 640, height: 640 }); // adjust final size

      // Convert the canvas to a Blob
      if (canvas) {
        canvas?.toBlob(
          (blob) => {
            if (blob) {
              // Convert Blob to File
              const croppedFile = new File([blob], "cropped-image.webp", {
                type: "image/webp",
              });

              setFile(croppedFile);

              formik.setFieldValue("image", croppedFile);

              setPreview(URL.createObjectURL(blob));

              toggleCropedOpen();
            }
          },
          "image/webp",
          0.6,
        ); // Specify the image format (e.g., webp) & Quality 60%
      }
    }
  };

  const handleAddImage = () => {
    const input = document.getElementById("file-input-ADD");
    if (input) input.click();
  };

  // const handleOutsideClick = useCallback(
  //   (event: MouseEvent) => {
  //     if (cropedOpen) return;
  //     if (
  //       modalRef.current &&
  //       !modalRef.current.contains(event.target as Node)
  //     ) {
  //       closeModal();
  //     }
  //   },
  //   [cropedOpen],
  // );

  // useEffect(() => {
  //   if (isModalOpen) {
  //     document.addEventListener("mousedown", handleOutsideClick);
  //   } else {
  //     document.removeEventListener("mousedown", handleOutsideClick);
  //   }

  //   return () => {
  //     document.removeEventListener("mousedown", handleOutsideClick);
  //   };
  // }, [handleOutsideClick, isModalOpen]);

  const validationSchema = Yup.object({
    branches: isAdmin
      ? Yup.array().min(1, t("addProductButton.branchRequired") as string)
      : Yup.array(),
    nameAR: Yup.string(),
    nameEN: Yup.string().test(
      "at-least-one-lang-name",
      t("addProductButton.nameRequired") as string,
      function (value) {
        const { nameAR } = this.parent;
        return Boolean(value?.trim() || nameAR?.trim());
      },
    ),
    image: Yup.mixed<File>()
      .required(t("addProductButton.imageRequired") as string)
      .test(
        "fileSize",
        t("addProductButton.fileTooLargeAlt", {
          maxSize:
            locale === "ar"
              ? MAX_IMAGE_SIZE_MB_String.ar
              : MAX_IMAGE_SIZE_MB_String.en,
        }) as string,
        (value) => {
          // Limit the file size to 10MB (10 * 1024 * 1024 bytes)
          return value && value.size <= MAX_IMAGE_SIZE;
        },
      ),
    price: Yup.number()
      .positive(t("addProductButton.pricePositive") as string)
      .required(t("addProductButton.priceRequired") as string),
    quantity: Yup.string()
      .min(0, t("addProductButton.quantityMin") as string)
      .required(t("addProductButton.quantityRequired") as string),
    categories: Yup.array().min(1, t("addProductButton.CategoryFill") as string),

    productCost: Yup.number().positive(
      t("addProductButton.productCostPositive") as string,
    ),
    lowPrice: Yup.number()
      .nullable()
      .test(
        "lowPrice-required-if-endDate",
        t("addProductButton.loweredPriceRequired") as string,
        function (value) {
          const { lowPriceEndDate } = this.parent;
          return lowPriceEndDate
            ? value !== null && value !== undefined && value > 0
            : true;
        },
      )
      .positive(t("addProductButton.loweredPricePositive") as string),
    lowPriceEndDate: Yup.date()
      .nullable()
      .test(
        "is-today-or-future",
        t("addProductButton.dateFutureOnly") as string,
        (value) =>
          !value ||
          new Date(value).setHours(0, 0, 0, 0) >=
            new Date().setHours(0, 0, 0, 0), // Ensure it's today or later
      )
      .test(
        "endDate-required-if-lowPrice",
        t("addProductButton.loweredPriceEndRequired") as string,
        function (value) {
          const { lowPrice } = this.parent;
          return lowPrice ? value !== undefined : true; // If lowPrice exists, end date is required
        },
      )
      .max(new Date("2200-12-31"), t("addProductButton.dateLimit") as string),
    descriptionAR: Yup.string(),
    descriptionEN: Yup.string().test(
      "at-least-one-lang-description",
      t("addProductButton.descRequired") as string,
      function (value) {
        const { descriptionAR } = this.parent;
        return Boolean(value?.trim() || descriptionAR?.trim());
      },
    ),
    
    calories: Yup.number().positive(t("addProductButton.mustBePositive") as string),
    salt: Yup.number().positive(t("addProductButton.mustBePositive") as string),
    caffeine: Yup.number().positive(t("addProductButton.mustBePositive") as string),

    addons: Yup.array().of(
      Yup.object().shape({
        nameAR: Yup.string(),
        nameEN: Yup.string().test(
          "at-least-one-lang-AddonName",
          t("addProductButton.nameRequired") as string,
          function (value) {
            const { nameAR } = this.parent;
            return Boolean(value?.trim() || nameAR?.trim());
          },
        ),
        price: Yup.number().min(
          0,
          t("addProductButton.priceNegative") as string,
        ),
      }),
    ),

    options: Yup.array().of(
      Yup.object().shape({
        nameAR: Yup.string(),
        nameEN: Yup.string().test(
          "at-least-one-lang-GroupName",
          t("addProductButton.nameRequired") as string,
          function (value) {
            const { nameAR } = this.parent;
            return Boolean(value?.trim() || nameAR?.trim());
          },
        ),
        limit: Yup.number()
          .required(t("addProductButton.groupLimitRequired") as string)
          .positive(t("addProductButton.groupLimitPositive") as string),
        options: Yup.array()
          .of(
            Yup.object().shape({
              nameAR: Yup.string(),
              nameEN: Yup.string().test(
                "at-least-one-lang-OptionName",
                t("addProductButton.nameRequired") as string,
                function (value) {
                  const { nameAR } = this.parent;
                  return Boolean(value?.trim() || nameAR?.trim());
                },
              ),
              price: Yup.number().min(
                0,
                t("addProductButton.priceNegative") as string,
              ),
            }),
          )
          .min(1, t("addProductButton.optionRequired") as string),
      }),
    ),
  });
  // Formik setup
  const formik = useFormik<FormValues>({
    initialValues: {
      nameAR: "",
      nameEN: "",
      categories: [],
      price: "",
      quantity: "",
      image: null,

      productCost: undefined,
      calories: "",
      salt: "",
      caffeine: "",
      lowPrice: "",
      lowPriceEndDate: "",
      descriptionAR: "",
      descriptionEN: "",
      addons: [],
      options: [],
      branches: [],
    },
    validationSchema,
    onSubmit: (values) => {
      const BASE_URL = import.meta.env.VITE_BASE_URL;
      const PostUrl = `${BASE_URL}/api/dashboard/products?locale=${locale}`;

      const branchesIds = values.branches?.map((branch) => branch.id);

      const IsUserSelectAllBranches =
        branchesIds?.length === allBranches?.length ? true : false;
      const FormatedLowPriceEndDate = values.lowPriceEndDate
        ? new Date(values.lowPriceEndDate).getTime() / 1000
        : undefined;

      const AddonsWithConvertZeroPriceToUndefined = values?.addons?.map(
        (addon) => ({
          ...addon,
          price: addon.price === 0 ? undefined : addon.price,
        }),
      );

      const OptionsWithConvertZeroPriceToUndefined = values?.options?.map(
        (option) => ({
          ...option,
          options: option?.options?.map((option) => ({
            ...option,
            price: option?.price === 0 ? undefined : option?.price,
          })),
        }),
      );

      const NewProduct = {
        nameAR: values.nameAR || "",
        nameEN: values.nameEN || "",
        categories:
          values.categories.length > 0 ? values.categories : undefined,
        price: values.price,
        quantity: values.quantity,
        descriptionAR: values.descriptionAR || "",
        descriptionEN: values.descriptionEN || "",

        productCost: values.productCost || undefined,
        calories: values.calories ? values.calories : undefined,
        salt: values.salt ? values.salt : undefined,
        caffeine: values.caffeine ? values.caffeine : undefined,
        lowPrice: values.lowPrice ? values.lowPrice : undefined,
        branches: isAdmin
          ? branchesIds?.length > 0
            ? branchesIds
            : undefined
          : undefined,
        allBranchesAvailable: isAdmin ? IsUserSelectAllBranches : undefined,
        lowPriceEndDate: FormatedLowPriceEndDate,
        addons:
          values.addons && values.addons?.length > 0
            ? AddonsWithConvertZeroPriceToUndefined
            : undefined,
        options:
          values.options && values.options?.length > 0
            ? OptionsWithConvertZeroPriceToUndefined
            : undefined,
        image: file,
        hidden: false,
      };

      async function createProduct() {
        const loadingToast = toast.loading(
          t("addProductButton.creating") as string,
        );
        setIsSending(true);
        try {
          const res = await axios.post(PostUrl, NewProduct, {
            headers: {
              Authorization: token,
              "Content-Type": "multipart/form-data",
            },
          });

          if (res.data.success === false) {
            toast.error(res.data.error);
            return;
          }

          toast.success(t("addProductButton.created") as string);

          setNeedRefetch(true);
        } catch (error: unknown) {
          if (axios.isAxiosError(error)) {
            const message =
              error.response?.data?.error ||
              (t("addProductButton.createFailed") as string);
            toast.error(message);
          } else {
            toast.error(t("addProductButton.error") as string);
          }
        } finally {
          toast.dismiss(loadingToast);
          setIsSending(false);
        }
      }

      createProduct();
    },
  });

  // Add Addon Handler
  const handleAddAddon = () => {
    const newAddon = { nameAR: "", nameEN: "", price: undefined };
    const newId = Date.now(); // Generate a unique ID
    setAddonIds((prevIds) => [newId,...prevIds]); // Store the ID
    formik.setFieldValue("addons", [newAddon,...formik.values.addons]);
  };

  // Add Group Handler
  const handleAddGroup = () => {
    const newGroup = { nameAR: "", nameEN: "", limit: 1, options: [] };
    formik.setFieldValue("options", [newGroup,...formik.values.options]);
  };

  const handleDeleteAddon = (index: number) => {
    setAddonIds((prevIds) => prevIds.filter((_, i) => i !== index)); // Remove the ID
    formik.setFieldValue(
      "addons",
      formik.values.addons.filter((_, i) => i !== index),
    );
  };

  const handleDeleteGroup = (index: number) => {
    formik.setFieldValue(
      "options",
      formik.values.options.filter((_, i) => i !== index),
    );
  };

  const handleDeleteSingleOption = (groupIndex: number, index: number) => {
    formik.setFieldValue(
      "options",
      formik.values.options.map((group, i) =>
        i === groupIndex
          ? { ...group, options: group.options.filter((_, i) => i !== index) }
          : group,
      ),
    );
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleOpenModal = () => {
    if (!allBranches.length)
      return toast.error(t("addProductButton.pleaseBranch") as string);
    if (!allUserCategories.length)
      return toast.error(t("addProductButton.pleaseCategory") as string);

    setIsModalOpen(true);
  };

  const isCaffineOrSaltOrCaloriesError = 
    (formik.errors.caffeine && formik.touched.caffeine) ||
    (formik.errors.salt && formik.touched.salt) ||
    (formik.errors.calories && formik.touched.calories);

  const showErrorsToast = () => {
      
    if(Object.keys(formik.errors).length > 0){
        const StringArrayErrors = flattenErrors(Object.values(formik.errors))
        const firstErrorMessage = StringArrayErrors[0]
      
          if (firstErrorMessage && (typeof firstErrorMessage === 'string')) {
            toast.error(firstErrorMessage as string);
          }
          }
    }

  return (
    <>
      {cropedOpen && (
        <div className="fixed left-0 top-0 z-[99999] flex h-full w-full flex-col items-center justify-center bg-black bg-opacity-50 px-5">
          <div className="flex w-full flex-col items-center gap-5 rounded-md bg-white px-4 py-8 md:w-[60%]">
            {preview && (
              <Cropper
                src={preview || ""}
                style={{ height: 500, width: "100%" }}
                aspectRatio={640 / 640}
                viewMode={1}
                zoomable={true}
                scalable={false}
                cropBoxResizable={false}
                cropBoxMovable={true}
                autoCropArea={1}
                ref={cropperRef}
              />
            )}
            <div className="text-center">
              {t("addProductButton.imageHintSimple")}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  onCrop();
                }}
                className="mt-2 rounded-md bg-sidebar px-5 py-2 text-base font-medium text-skyblue"
              >
                {t("addProductButton.save")}
              </button>
              <button
                type="button"
                className="mt-2 rounded-md bg-error px-5 py-2 text-base font-medium text-white"
                onClick={onCloseCropper}
              >
                {t("addProductButton.close")}
              </button>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={formik.handleSubmit} id="add-product-form">
        <button
          type="button"
          onClick={handleOpenModal}
          className="flex items-center justify-center gap-2 rounded-full border border-border bg-sidebar px-3 py-2 text-sm font-semibold text-skyblue md:gap-3"
        >
          <BsPlusCircleFill size={22} />
          <span>{t("addProductButton.addProduct")}</span>
          <MdKeyboardArrowDown size={19} />
        </button>

        {isModalOpen && (
          <div
            className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/50 pt-[70px]"
            aria-labelledby="modal-title"
            role="dialog"
            aria-modal="true"
          >
            <div
              ref={modalRef}
              className="flex w-[95%] max-w-3xl flex-col gap-3 rounded-lg bg-white shadow-lg"
            >
              <div className="flex items-center justify-between px-2 py-3 sm:px-4 sm:py-4">
                <div className="flex items-center gap-3 text-sm font-semibold sm:text-base md:text-2xl">
                  <h2 className="text-lg font-semibold">
                    {t("addProductButton.addProduct")}
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={closeModal}
                  className="mb-3 text-gray-600 hover:text-gray-800"
                >
                  <IoMdClose size={24} className="text-red-600" />
                </button>
              </div>

              {/* start body */}
              <div className="thin-scrollbar flex max-h-[55dvh] flex-col gap-5 overflow-y-auto px-3 py-3 sm:px-5 sm:py-4">
                {/* Product Name */}
                <div className="flex flex-col gap-1">
                  <label>{t("addProductButton.productName")}</label>
                  <ProductName
                    place="modal"
                    productNames={{
                      ar: formik.values.nameAR,
                      en: formik.values.nameEN,
                    }}
                    onChange={(newValues) => {
                      formik.setFieldValue("nameAR", newValues.ar);
                      formik.setFieldValue("nameEN", newValues.en);
                    }}
                  />
                  {formik.touched.nameAR && formik.errors.nameAR && (
                    <div className="text-xs text-red-500">
                      {formik.errors.nameAR}
                    </div>
                  )}

                  {formik.touched.nameEN && formik.errors.nameEN && (
                    <div className="text-xs text-red-500">
                      {formik.errors.nameEN}
                    </div>
                  )}
                </div>

                {/* Product Description */}
                <div className="space-y-1">
                  <label className="" htmlFor="productDescription">
                    {t("addProductButton.description")}
                  </label>
                  <ProductDescription
                    productDescriptions={{
                      ar: formik.values.descriptionAR,
                      en: formik.values.descriptionEN,
                    }}
                    onDescriptionChange={(lang, value) =>
                      formik.setFieldValue(
                        `description${lang.toUpperCase()}`,
                        value,
                      )
                    }
                  />
                  {formik.errors.descriptionAR &&
                    formik.touched.descriptionAR && (
                      <div className="text-sm text-red-600">
                        {formik.errors.descriptionAR}
                      </div>
                    )}
                  {formik.errors.descriptionEN &&
                    formik.touched.descriptionEN && (
                      <div className="text-sm text-red-600">
                        {formik.errors.descriptionEN}
                      </div>
                    )}
                </div>

                {/* Product Price */}
                <div className="flex flex-col gap-1">
                  <label>{t("addProductButton.price")}</label>
                  <PriceInput
                    place="modal"
                    value={formik.values.price}
                    onChange={(value) => formik.setFieldValue("price", value)}
                  />
                  {formik.touched.price && formik.errors.price && (
                    <div className="text-xs text-red-500">
                      {formik.errors.price}
                    </div>
                  )}
                </div>

                {/* Product Quantity */}
                <div className="flex flex-col gap-1">
                  <label>{t("addProductButton.quantity")}</label>
                  <QuantityInput
                    place="modal"
                    quantity={formik.values.quantity}
                    onChange={(value) =>
                      formik.setFieldValue("quantity", value)
                    }
                  />
                  {formik.touched.quantity && formik.errors.quantity && (
                    <div className="text-xs text-red-500">
                      {formik.errors.quantity}
                    </div>
                  )}
                </div>

                {/* Product Categories */}
                <div className="flex flex-col gap-1">
                  <label>{t("addProductButton.categories")}</label>
                  <CategoryInput
                    place="modal"
                    allUserCategories={allUserCategories}
                    userPrevSelectedCategories={[]}
                    placeholder={
                      t("addProductButton.categoriesLabel") as string
                    }
                    onCategoryChange={(selectedCategories) =>
                      formik.setFieldValue("categories", selectedCategories)
                    }
                    error={
                      formik.touched.categories && formik.errors.categories
                        ? Array.isArray(formik.errors.categories)
                          ? formik.errors.categories.join(", ")
                          : formik.errors.categories
                        : undefined
                    }
                  />
                </div>

                {/* Product Branches */}
                {isAdmin && (
                  <div className="mb-2 flex flex-col gap-1">
                    <label className="">
                      {t("addProductButton.branchesWhereAvailable")}
                    </label>
                    <ProductBranchesInput
                      allUserBranches={allBranches}
                      userPrevSelectedBranches={[]}
                      placeholder={
                        t("addProductButton.branchesLabel") as string
                      }
                      onBranchChange={(selectedBranches) =>
                        formik.setFieldValue("branches", selectedBranches)
                      }
                      error={
                        formik.touched.branches && formik.errors.branches
                          ? Array.isArray(formik.errors.branches)
                            ? formik.errors.branches.join(", ")
                            : formik.errors.branches
                          : undefined
                      }
                    />
                  </div>
                )}

                {/* Product Image */}
                <div className="flex flex-col gap-1">
                  <div className="flex items-center justify-between gap-4">
                    <button
                      onClick={handleAddImage}
                      type="button"
                      className="cursor-pointer rounded-md bg-sidebar px-4 py-2 text-skyblue"
                    >
                      {t(
                        file
                          ? "addProductButton.changeImage"
                          : "addProductButton.addImage",
                      )}
                    </button>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                      id="file-input-ADD"
                    />

                    <div className="flex max-w-24 flex-col items-center justify-center gap-1">
                      <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-lg border border-gray-300">
                        <img
                          src={
                            preview ||
                            "/images/placeholders/placeholderProductImage.webp"
                          }
                          alt="Preview"
                          className="h-full w-full object-cover"
                        />
                      </div>
                      <p className="text-center text-xs text-slate-400">
                        {t("addProductButton.imageSizeMax", {
                          maxSize:
                            locale === "ar"
                              ? MAX_IMAGE_SIZE_MB_String.ar
                              : MAX_IMAGE_SIZE_MB_String.en,
                        })}
                      </p>
                    </div>
                  </div>
                  {formik.errors.image && formik.touched.image && (
                    <div className="text-sm text-red-600">
                      {formik.errors.image}
                    </div>
                  )}
                </div>

                {/* Product Cost */}
                <div className="flex flex-col gap-1">
                  <label>{t("addProductButton.cost")}</label>
                  <ProductCostInput
                    place="modal"
                    value={formik.values.productCost as number}
                    onChange={(value) =>
                      formik.setFieldValue("productCost", value)
                    }
                  />
                  {formik.touched.productCost && formik.errors.productCost && (
                    <div className="text-xs text-red-500">
                      {formik.errors.productCost}
                    </div>
                  )}
                </div>

                {/* Data Modal Copied without description */}

                <div className="grid size-full grid-cols-2 gap-3">
                                    {/* Lowered Price */}
                  <div className="col-start-1 col-end-2 size-full space-y-1">
                    <label htmlFor="lowPrice">
                      {t("addProductButton.loweredPrice")}
                    </label>
                    <input
                      type="number"
                      name="lowPrice"
                      placeholder={t("addProductButton.loweredPrice") as string}
                      value={
                        isLowPriceFoucused
                          ? formik.values.lowPrice
                          : formatMoneyForInput(formik.values.lowPrice)
                      }
                      onFocus={() => setIsLowPriceFocused(true)}
                      onBlur={() => setIsLowPriceFocused(false)}
                      onChange={formik.handleChange}
                      className="size-full h-12 rounded-sm border px-2"
                      step={0.1}
                    />
                    {formik.errors.lowPrice && formik.touched.lowPrice && (
                      <div className="text-sm text-red-600">
                        {formik.errors.lowPrice}
                      </div>
                    )}
                  </div>
                  {/* Lowered Price End Date */}
                  <div className="relative col-start-2 col-end-3 size-full space-y-1">
                    <label htmlFor="lowPriceEndDate">
                      {t("addProductButton.loweredPriceEnd")}
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        name="lowPriceEndDate"
                        value={formik.values.lowPriceEndDate}
                        onChange={(e) => {
                          const value = e.target.value;
                          formik.setFieldValue("lowPriceEndDate", value);
                        }}
                        className="size-full h-12 rounded-sm border px-2"
                      />
                      {formik.values.lowPriceEndDate && (
                        <button
                          type="button"
                          onClick={() =>
                            formik.setFieldValue("lowPriceEndDate", "")
                          }
                          className="absolute right-[2.5rem] top-1/2 -translate-y-1/2 transform text-2xl text-red-400 hover:text-red-700"
                        >
                          &times;
                        </button>
                      )}
                    </div>

                    {formik.errors.lowPriceEndDate &&
                      formik.touched.lowPriceEndDate && (
                        <div className="text-sm text-red-600">
                          {formik.errors.lowPriceEndDate}
                        </div>
                      )}
                  </div>


                </div>

                {/* Product Options */}
                <div className="mt-5 flex size-full flex-col gap-5 rounded-md border p-5">
                  <h1 className="mb-1 text-xl font-semibold text-black">
                    {t("addProductButton.productOptions")}{" "}
                  </h1>
                  <button
                    type="button"
                    className="mb-2 me-2 size-fit rounded-md bg-sidebar px-5 py-2 text-sm font-medium text-skyblue"
                    onClick={handleAddGroup}
                  >
                    {t("addProductButton.addOptionGroup")}
                  </button>
                  {formik.values.options.map((group, index) => (
                    <ProductOptionGroup
                      key={index}
                      onDeleteGroup={handleDeleteGroup}
                      onDeleteOption={handleDeleteSingleOption}
                      groupIndex={index}
                      {...group}
                      onChange={(newGroup) =>
                        formik.setFieldValue("options", [
                          ...formik.values.options.slice(0, index),
                          newGroup,
                          ...formik.values.options.slice(index + 1),
                        ])
                      }
                      errorMustFillOneOption={
                        formik.values.options[index]?.options?.length === 0
                          ? (t("addProductButton.optionFill") as string)
                          : ""
                      }
                      errorNameAR={
                        (
                          formik.errors.options?.[
                            index
                          ] as FormikErrors<OptionGroup>
                        )?.nameAR || ""
                      }
                      errorNameEN={
                        (
                          formik.errors.options?.[
                            index
                          ] as FormikErrors<OptionGroup>
                        )?.nameEN || ""
                      }
                      errorLimit={
                        (
                          formik.errors.options?.[
                            index
                          ] as FormikErrors<OptionGroup>
                        )?.limit || ""
                      }
                      errorOptions={
                        ((
                          formik.errors.options?.[
                            index
                          ] as FormikErrors<OptionGroup>
                        )?.options || []) as FormikErrors<option>[]
                      }
                    />
                  ))}
                </div>

                {/* Product Addons */}
                <div className="mt-5 size-full rounded-md border p-5">
                  <h1 className="mb-5 text-xl font-semibold text-black">
                    {t("addProductButton.addProduct")}
                  </h1>
                  <button
                    type="button"
                    className="mb-6 me-2 rounded-md bg-sidebar px-5 py-2 text-sm font-medium text-skyblue"
                    onClick={handleAddAddon}
                  >
                    {t("addProductButton.addAddon")}{" "}
                  </button>

                  <div className="flex flex-col gap-3">
                    {formik.values.addons?.length > 0 ? (
                      <h3 className="text-lg font-semibold">
                        {t("addProductButton.addons")}{" "}
                      </h3>
                    ) : null}
                    {formik.values.addons.map((addon, index) => (
                      <ProductAddon
                        key={addonIds[index]} // Use the ID as the key
                        {...addon}
                        onChange={(newAddon) =>
                          formik.setFieldValue("addons", [
                            ...formik.values.addons.slice(0, index),
                            newAddon,
                            ...formik.values.addons.slice(index + 1),
                          ])
                        }
                        onDelete={handleDeleteAddon}
                        index={index}
                        error={
                          formik.errors.addons?.[index] &&
                          typeof formik.errors.addons[index] !== "string"
                            ? formik.errors.addons[index].nameAR ||
                              formik.errors.addons[index].nameEN ||
                              formik.errors.addons[index].price
                            : undefined
                        }
                      />
                    ))}
                  </div>
                </div>

                <div className={`mt-5 rounded-md border ${isCaffineOrSaltOrCaloriesError && !isNutritionOpen ? "border-red-500 bg-red-50" : ""}`}>
                  {/* Accordion Header */}
                  <button
                    type="button"
                    className="flex w-full items-center justify-between p-5 text-left"
                    onClick={() => setIsNutritionOpen(!isNutritionOpen)}
                  >
                    <h1 className="text-xl font-semibold text-black">
                      {t("addProductButton.nutritions-info")}
                    </h1>
                    <svg
                      className={`h-5 w-5 transform transition-transform duration-200 ${
                        isNutritionOpen ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {/* Accordion Content (Nutrition) */}
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      isNutritionOpen ? "max-h-[500px] p-5" : "max-h-0 p-0"
                    }`}
                  >
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {/* Calories */}
                      <div className="space-y-1">
                        <label htmlFor="calories">
                          {t("addProductButton.calories")}
                        </label>
                        <input
                          type="number"
                          name="calories"
                          placeholder={t("addProductButton.calories") as string}
                          value={formik.values.calories}
                          onChange={formik.handleChange}
                          className="h-12 w-full rounded-sm border px-2"
                        />
                        {formik.errors.calories && formik.touched.calories && (
                          <div className="text-sm text-red-600">
                            {formik.errors.calories}
                          </div>
                        )}
                      </div>

                      {/* Salt */}
                      <div className="space-y-1">
                        <label htmlFor="salt">
                          {t("addProductButton.salt")}
                        </label>
                        <input
                          type="number"
                          name="salt"
                          placeholder={
                            t("addProductButton.salt_label") as string
                          }
                          value={formik.values.salt}
                          onChange={formik.handleChange}
                          className="h-12 w-full rounded-sm border px-2"
                        />
                        {formik.errors.salt && formik.touched.salt && (
                          <div className="text-sm text-red-600">
                            {formik.errors.salt}
                          </div>
                        )}
                      </div>

                      {/* Caffeine */}
                      <div className="space-y-1">
                        <label htmlFor="caffeine">
                          {t("addProductButton.caffeine")}
                        </label>
                        <input
                          type="number"
                          name="caffeine"
                          placeholder={
                            t("addProductButton.caffeine_label") as string
                          }
                          value={formik.values.caffeine}
                          onChange={formik.handleChange}
                          className="h-12 w-full rounded-sm border px-2"
                        />
                        {formik.errors.caffeine && formik.touched.caffeine && (
                          <div className="text-sm text-red-600">
                            {formik.errors.caffeine}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                </div>
              </div>
              {/* end body */}
              <div className="mt-6 flex justify-end px-2 py-3 sm:px-4 sm:py-4">
                <button
                onClick={showErrorsToast}
                  disabled={isSending}
                  form="add-product-form"
                  type="submit"
                  className="w-fit rounded-md bg-sidebar px-4 py-2 text-sm font-semibold text-skyblue disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400 md:text-base"
                >
                  {t("addProductButton.addNewProduct")}
                </button>
              </div>
            </div>
          </div>
        )}
      </form>
    </>
  );
}
